import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TOOL_CATEGORIES, FREE_TOOL_KEYS } from './constants';
import { ListToolCard } from './ToolCards';

// Category Section Component
export const CategorySection = ({
  categoryKey,
  tools,
  pinnedToolKeys,
  expandedCategories,
  onToggleCategory,
  onToolClick,
  onTogglePin,
  onToggleHide,
  toolBadges,
  hasPremiumAccess,
  canAccessPremiumTool // Function to check tool-specific access
}) => {
  const category = TOOL_CATEGORIES[categoryKey];
  if (!category || tools.length === 0) return null;

  const CategoryIcon = category.icon;
  const isExpanded = expandedCategories[categoryKey] !== false;

  // Don't show pinned tools in categories (they're at the top)
  const nonPinnedTools = tools.filter(t => !pinnedToolKeys.includes(t.key));
  if (nonPinnedTools.length === 0) return null;

  return (
    <div className="mb-8">
      <button
        onClick={() => onToggleCategory(categoryKey)}
        className="flex items-center gap-3 mb-4 w-full text-left group"
      >
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center",
          `bg-gradient-to-br ${category.color}`
        )}>
          <CategoryIcon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
          <p className="text-xs text-gray-500">{category.description}</p>
        </div>
        <span className="text-xs text-gray-400 mr-2">{nonPinnedTools.length} tools</span>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
        )}
      </button>

      {isExpanded && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {nonPinnedTools.map(tool => {
            const isPremium = tool.userTool && !FREE_TOOL_KEYS.includes(tool.key);
            const hasToolAccess = canAccessPremiumTool ? canAccessPremiumTool(tool.key) : hasPremiumAccess;
            
            return (
              <ListToolCard
                key={tool.key}
                tool={tool}
                isPinned={false}
                onToolClick={onToolClick}
                onTogglePin={onTogglePin}
                onToggleHide={onToggleHide}
                badge={toolBadges?.[tool.key]}
                isPremium={isPremium}
                isLocked={isPremium && !hasToolAccess}
                hasPremiumAccess={hasToolAccess}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
